// frontend_react/src/pages/supplierInvoice/SupplierInvoiceValidationPage.jsx

import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import {
  getSupplierInvoice,
  validateSupplierInvoice,
  smartMatchProduct,
  createProductFromSupplierInvoiceLine,
  learnSupplierInvoiceMatching,
} from "../../services/supplierInvoiceApi";
import { getSocket } from "../../services/socket";
import ProductAutocomplete from "./ProductAutocomplete";
import { useAdminScope } from "../../context/AdminScopeContext";

export default function SupplierInvoiceValidationPage() {
  const { invoiceUuid } = useParams();
  const { selectedCompanyUuid } = useAdminScope();

  const [invoice, setInvoice] = useState(null);
  const [lines, setLines] = useState([]);
  const [stats, setStats] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalLines, setTotalLines] = useState(0);

  const [autoMatched, setAutoMatched] = useState(false);
  const [error, setError] = useState(null);

  const [creatingLineUuid, setCreatingLineUuid] = useState(null);
  const [sellingPrices, setSellingPrices] = useState({});

  const loadInvoice = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAutoMatched(false);

    try {
      const res = await getSupplierInvoice(invoiceUuid, {
        companyUuid: selectedCompanyUuid,
      });

      setInvoice(res.data.invoice);
      setLines(res.data.lines || []);
      setStats(res.data.stats);
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement de la facture.");
    } finally {
      setLoading(false);
    }
  }, [invoiceUuid, selectedCompanyUuid]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  useEffect(() => {
    const socket = getSocket();
    const room = `invoice_${invoiceUuid}`;

    socket.emit("join", room);

    socket.on("invoice:processing", (data) => {
      if (data.invoice_uuid !== invoiceUuid) return;

      setProcessing(true);
      setProgress(0);
      setLines([]);
    });

    socket.on("invoice:line", (data) => {
      if (data.invoice_uuid !== invoiceUuid) return;

      setTotalLines(data.total || 0);

      setLines((prev) => {
        const updated = [...prev];

        updated[data.index] = {
          ...updated[data.index],
          uuid: data.uuid || updated[data.index]?.uuid,
          designation: data.designation,
          quantity: data.quantity,
          unit_price_ht: data.unit_price_ht,
          total_ht: data.total_ht,
          vat_rate: data.vat_rate,
          is_matched: data.matched,
          match_confidence: data.confidence,
        };

        return updated;
      });

      setProgress(data.progress || 0);
    });

    socket.on("invoice:done", (data) => {
      if (data.invoice_uuid !== invoiceUuid) return;

      setProcessing(false);
      loadInvoice();
    });

    return () => {
      socket.off("invoice:processing");
      socket.off("invoice:line");
      socket.off("invoice:done");
    };
  }, [invoiceUuid, loadInvoice]);

  const updateLine = (lineUuid, field, value) => {
    setLines((prev) =>
      prev.map((line) =>
        line.uuid === lineUuid ? { ...line, [field]: value } : line
      )
    );
  };

  useEffect(() => {
    if (!lines.length || autoMatched || processing) return;

    const autoMatch = async () => {
      setAutoMatched(true);

      await Promise.all(
        lines.map(async (line, index) => {
          if (!line || line.is_matched || line.is_suggested) return;

          try {
            const res = await smartMatchProduct(
              {
                designation: line.designation,
                ean: line.ean,
                supplier_ref: line.supplier_ref,
              },
              {
                companyUuid: selectedCompanyUuid,
              }
            );

            const data = res.data;

            if (data.type === "existing" && data.best_match?.score > 0.6) {
              const match = data.best_match;

              setLines((prev) =>
                prev.map((l, i) =>
                  i === index
                    ? {
                        ...l,
                        product_uuid: match.uuid,
                        is_matched: true,
                        match_confidence: match.score,
                        match_method: match.method,
                      }
                    : l
                )
              );
            }

            if (data.type === "suggestion") {
              setLines((prev) =>
                prev.map((l, i) =>
                  i === index
                    ? {
                        ...l,
                        suggestion: data.suggestion,
                        is_suggested: true,
                      }
                    : l
                )
              );
            }
          } catch (err) {
            console.error("Erreur smart match :", err);
          }
        })
      );
    };

    autoMatch();
  }, [lines, autoMatched, processing]);

  const updateSellingPrice = (lineUuid, value) => {
    setSellingPrices((prev) => ({
      ...prev,
      [lineUuid]: value,
    }));
  };

  const createProductFromInvoiceLine = async (line) => {
    const sellingPriceTtc = Number(
      String(sellingPrices[line.uuid] || "").replace(",", ".")
    );

    if (!sellingPriceTtc || sellingPriceTtc <= 0) {
      setError("Renseigne un prix de vente TTC avant de créer le produit.");
      return;
    }

    setCreatingLineUuid(line.uuid);
    setError(null);

    try {
      const res = await createProductFromSupplierInvoiceLine(
        {
          supplier_invoice_line_uuid: line.uuid,
          designation: line.designation,
          ean: line.ean,
          reference: line.supplier_ref,
          supplier_reference: line.supplier_ref,
          distributor: invoice?.supplier_name || "ASMODEE FRANCE",
          unit_cost_ht: line.unit_price_ht,
          vat_rate: line.vat_rate,
          selling_price_ttc: sellingPriceTtc,
        },
        {
          companyUuid: selectedCompanyUuid,
        }
      );

      const newUuid = res.data.uuid || res.data.product_uuid;

      if (!newUuid) {
        throw new Error("UUID produit absent de la réponse backend.");
      }

      await learnSupplierInvoiceMatching(
        {
          designation: line.designation,
          supplier_ref: line.supplier_ref,
          ean: line.ean,
          product_uuid: newUuid,
          supplier_name: invoice?.supplier_name || "ASMODEE FRANCE",
          source: "user_create",
          confidence: 1,
        },
        {
          companyUuid: selectedCompanyUuid,
        }
      );

      updateLine(line.uuid, "product_uuid", newUuid);
      updateLine(line.uuid, "is_matched", true);
      updateLine(line.uuid, "is_suggested", false);
      updateLine(line.uuid, "match_method", "user_create");
      updateLine(line.uuid, "match_confidence", 1);
    } catch (err) {
      console.error("Erreur création produit :", err);
      setError("Erreur lors de la création du produit.");
    } finally {
      setCreatingLineUuid(null);
    }
  };

  const handleValidate = async () => {
    setSaving(true);
    setError(null);

    try {
      const payload = lines.map((line) => ({
        uuid: line.uuid,
        product_uuid: line.product_uuid,
      }));

      await validateSupplierInvoice(invoiceUuid, payload, {
        companyUuid: selectedCompanyUuid,
      });
      await loadInvoice();
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la validation de la facture.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <Panel>
          <LoadingText>Chargement de la facture...</LoadingText>
        </Panel>
      </Page>
    );
  }

  return (
    <Page>
      <Panel>
        <Header>
          <div>
            <Title>VALIDATION FACTURE</Title>
            <Subtitle>
              {invoice?.supplier_name || "Fournisseur inconnu"} —{" "}
              {invoice?.invoice_number || "Numéro inconnu"}
            </Subtitle>
          </div>

          <StatusBadge $review={invoice?.needs_review || processing}>
            {processing ? "Traitement en cours" : invoice?.needs_review ? "À vérifier" : "OK"}
          </StatusBadge>
        </Header>

        {processing && (
          <ProgressBar>
            <div style={{ width: `${(progress / (totalLines || 1)) * 100}%` }} />
          </ProgressBar>
        )}

        {error && <ErrorBox>{error}</ErrorBox>}

        <SummaryGrid>
          <SummaryCard>
            <Label>Date</Label>
            <Value>{invoice?.invoice_date || "-"}</Value>
          </SummaryCard>

          <SummaryCard>
            <Label>Total HT</Label>
            <Value>{invoice?.total_ht?.toFixed?.(2) || "-"} €</Value>
          </SummaryCard>

          <SummaryCard>
            <Label>TVA</Label>
            <Value>{invoice?.total_tva?.toFixed?.(2) || "-"} €</Value>
          </SummaryCard>

          <SummaryCard>
            <Label>Total TTC</Label>
            <Value>{invoice?.total_ttc?.toFixed?.(2) || "-"} €</Value>
          </SummaryCard>

          <SummaryCard>
            <Label>Matching</Label>
            <Value>
              {stats?.matched_lines || 0}/{stats?.total_lines || lines.length}
            </Value>
          </SummaryCard>
        </SummaryGrid>

        {stats?.unmatched_lines > 0 && (
          <Warning>
            {stats.unmatched_lines} ligne(s) non matchée(s). Associe les produits puis valide la facture.
          </Warning>
        )}

        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Statut</th>
                <th>Désignation</th>
                <th>Qté</th>
                <th>PU HT</th>
                <th>Total HT</th>
                <th>TVA</th>
                <th>Match</th>
                <th>Produit</th>
              </tr>
            </thead>

            <tbody>
              {lines.map((line, index) => (
                <tr key={line?.uuid || index}>
                  <td>
                    <LineBadge
                      $matched={line?.is_matched}
                      $suggested={line?.is_suggested}
                      $needsCreation={line?.match_status === "needs_creation"}
                    >
                      {line?.is_matched
                        ? "Matché"
                        : line?.match_status === "needs_creation"
                        ? "À créer"
                        : line?.is_suggested
                        ? "Suggestion"
                        : "À corriger"
                      }
                    </LineBadge>
                  </td>

                  <td>{line?.designation || "..."}</td>
                  <td>{line?.quantity || "-"}</td>
                  <td>{line?.unit_price_ht?.toFixed?.(2) || "-"} €</td>
                  <td>{line?.total_ht?.toFixed?.(2) || "-"} €</td>
                  <td>{line?.vat_rate || "-"}%</td>

                  <td>
                    {line?.match_method || "-"}{" "}
                    {line?.match_confidence
                      ? `(${Math.round(line.match_confidence * 100)}%)`
                      : ""}
                  </td>

                  <td>
                    <ProductAutocomplete
                      value={line.product_uuid}
                      line={line}
                      onSelect={(product) => {
                        updateLine(line.uuid, "product_uuid", product.uuid);
                        updateLine(line.uuid, "is_matched", true);
                        updateLine(line.uuid, "is_suggested", false);
                        updateLine(line.uuid, "match_method", "user");
                        updateLine(line.uuid, "match_confidence", 1);
                      }}
                    />
                    {!line.is_matched && (
                      <CreateProductBox>
                        <PriceInput
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Prix vente TTC"
                          value={sellingPrices[line.uuid] || ""}
                          onChange={(e) => updateSellingPrice(line.uuid, e.target.value)}
                        />

                        <CreateButton
                          type="button"
                          onClick={() => createProductFromInvoiceLine(line)}
                          disabled={creatingLineUuid === line.uuid}
                        >
                          {creatingLineUuid === line.uuid ? "Création..." : "Créer produit"}
                        </CreateButton>
                      </CreateProductBox>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>

        <Actions>
          <SecondaryButton onClick={loadInvoice} disabled={saving || processing}>
            Recharger
          </SecondaryButton>

          <PrimaryButton
            onClick={handleValidate}
            disabled={saving || processing || lines.some((line) => !line.is_matched)}
          >
            {saving ? "Validation..." : "Valider la facture"}
          </PrimaryButton>
        </Actions>
      </Panel>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  padding: 32px;
  background: #071022;
  color: white;
`;

const Panel = styled.div`
  background: #0b1329;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 24px;
  background: #0b1329;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 32px;
  font-weight: 600;
`;

const Subtitle = styled.div`
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.6);
`;

const StatusBadge = styled.span`
  padding: 10px 18px;
  border-radius: 999px;
  background: ${({ $review }) => ($review ? "#7a4b00" : "#14532d")};
  color: white;
  font-weight: 600;
`;

const SummaryGrid = styled.div`
  padding: 20px;
  background: #15203f;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
`;

const SummaryCard = styled.div`
  background: #0b1329;
  border-radius: 20px;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Label = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
`;

const Value = styled.div`
  margin-top: 8px;
  font-size: 22px;
  font-weight: 700;
`;

const Warning = styled.div`
  margin: 20px;
  padding: 16px;
  border-radius: 16px;
  background: #3b2f00;
  border: 1px solid #8a5a00;
  color: #facc15;
`;

const ErrorBox = styled.div`
  margin: 0 20px 20px;
  padding: 16px;
  border-radius: 16px;
  background: #3b0d0d;
  border: 1px solid #7f1d1d;
  color: #fca5a5;
`;

const TableWrapper = styled.div`
  margin: 20px;
  overflow-x: auto;
  background: #0b1329;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    text-align: left;
    vertical-align: middle;
  }

  th {
    color: rgba(255, 255, 255, 0.6);
    font-size: 13px;
    font-weight: 600;
  }

  td {
    color: white;
  }

  tbody tr:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const LineBadge = styled.span`
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ $matched, $suggested, $needsCreation }) =>
    $matched
      ? "#14532d"
      : $needsCreation
      ? "#b45309"
      : $suggested
      ? "#7a4b00"
      : "#7f1d1d"};
  color: white;
  font-size: 12px;
  font-weight: 600;
`;

const Actions = styled.div`
  padding: 20px;
  background: #15203f;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const SecondaryButton = styled.button`
  padding: 14px 22px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: #0b1329;
  color: white;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(SecondaryButton)`
  background: #4ea3e9;
  border-color: #4ea3e9;
  font-weight: 700;
`;

const ProgressBar = styled.div`
  height: 8px;
  margin: 0 20px 20px;
  background: #0b1329;
  border-radius: 999px;
  overflow: hidden;

  div {
    height: 100%;
    background: #4ea3e9;
    transition: width 0.2s ease;
  }
`;

const CreateButton = styled.button`
  margin-top: 0;
  padding: 8px 12px;
  border-radius: 10px;
  background: #b45309;
  border: none;
  color: white;
  cursor: pointer;

  &:hover {
    background: #d97706;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const LoadingText = styled.div`
  padding: 40px;
  font-size: 22px;
`;

const CreateProductBox = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PriceInput = styled.input`
  width: 130px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: #15203f;
  color: white;
  outline: none;

  &:focus {
    border-color: #4ea3e9;
  }
`;